/**
 * WellHaus — Rebuy Smart Cart Shipping Progress Bar  (v2)
 * ========================================================
 *
 * Lives in: assets/wh-cart-shipping-bar.js
 * Loaded from: layout/theme.liquid
 * Renders into: <div id="wh-cart-ship-bar"></div>  (Rebuy Custom Code Block)
 *
 * Requires this global, set in theme.liquid ABOVE the script tag:
 *   window.WH_IS_MEMBER = true | false;
 *
 * ---------------------------------------------------------------------
 * WHAT CHANGED IN v2
 * ---------------------------------------------------------------------
 * 1. FILL BAR: the progress is now painted directly onto the track via an
 *    inline linear-gradient with !important. There is no longer a child
 *    "fill" div. This removes the percentage-width resolution failure that
 *    broke the product-page version, and it cannot be overridden by Rebuy's
 *    stylesheet. Animation is done with requestAnimationFrame instead of a
 *    CSS transition, because gradients don't transition.
 *
 * 2. MEMBERSHIP ADD: now detects whether the membership variant requires a
 *    selling plan (subscription) and includes it in the add payload. Surfaces
 *    real errors to the console, always restores the button label, and falls
 *    back to the product page if the AJAX add is rejected.
 * ---------------------------------------------------------------------
 *
 * Console diagnostics: run  whCartBarDebug()
 * Force an add test:   run  whCartBarTestAdd()
 */
(function () {
  'use strict';

  if (window.__whCartShipBar) return;
  window.__whCartShipBar = true;

  var CONFIG = {
    mountId: 'wh-cart-ship-bar',

    marketplaceHandle: 'wellhaus-marketplace',
    merchHandle: 'wellhaus-merch',
    boxHandles: [
      'wh-seasonal-box',
      'wh-boxes',
      'clean-collab-hub',
      'wh-holiday-boxes'
    ],

    membershipVariantId: 46185482256628,
    membershipHandle: 'wellhaus-membership',

    nonMemberThreshold: 11000, // $110.00 in cents
    memberThreshold: 7500,     // $75.00  in cents

    standardFlatRate: '$9.99',
    boxFlatRate: '$15.00',

    cachePrefix: 'wh-coll-v1-',
    cacheTtlMs: 1000 * 60 * 30,

    color: {
      teal: '#315144',
      chartreuse: '#798440',
      lemon: '#C5B04E',
      beige: '#EFEED7',
      clay: '#CABDA8',
      azul: '#A5B8D4',
      espresso: '#323333'
    }
  };

  var state = {
    cart: null,
    collections: {},
    ready: false,
    rootNode: null,
    writing: false,
    paintedPercent: 0,
    tweenId: null,
    lastAddError: null
  };

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------
  function money(cents) {
    var v = (Math.max(0, cents) / 100).toFixed(2);
    return '$' + v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function readCache(handle) {
    try {
      var raw = sessionStorage.getItem(CONFIG.cachePrefix + handle);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.t > CONFIG.cacheTtlMs) return null;
      return parsed.ids;
    } catch (e) {
      return null;
    }
  }

  function writeCache(handle, ids) {
    try {
      sessionStorage.setItem(
        CONFIG.cachePrefix + handle,
        JSON.stringify({ t: Date.now(), ids: ids })
      );
    } catch (e) { /* non-fatal */ }
  }

  function fetchCollectionIds(handle) {
    var cached = readCache(handle);
    if (cached) return Promise.resolve(cached);

    var ids = [];
    function page(n) {
      return fetch(
        '/collections/' + handle + '/products.json?limit=250&page=' + n,
        { credentials: 'same-origin', headers: { Accept: 'application/json' } }
      )
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data && data.products) || [];
          products.forEach(function (p) { ids.push(p.id); });
          if (products.length === 250 && n < 10) return page(n + 1);
          return ids;
        })
        .catch(function () { return ids; });
    }

    return page(1).then(function (result) {
      writeCache(handle, result);
      return result;
    });
  }

  function loadAllCollections() {
    var handles = [CONFIG.marketplaceHandle, CONFIG.merchHandle].concat(CONFIG.boxHandles);
    return Promise.all(
      handles.map(function (h) {
        return fetchCollectionIds(h).then(function (ids) {
          state.collections[h] = new Set(ids);
        });
      })
    );
  }

  function inCollection(productId, handle) {
    var set = state.collections[handle];
    return !!(set && set.has(productId));
  }

  function inAnyBox(productId) {
    for (var i = 0; i < CONFIG.boxHandles.length; i++) {
      if (inCollection(productId, CONFIG.boxHandles[i])) return true;
    }
    return false;
  }

  // -------------------------------------------------------------------
  // Cart math
  // -------------------------------------------------------------------
  /**
   * Classifies every line item into EXACTLY ONE bucket.
   *
   * This matters because products can belong to more than one collection —
   * The Everyday Tumbler, for example, is in both wellhaus-marketplace and
   * wellhaus-merch. Summing the two collections independently would count
   * that product twice. Precedence is: box > merch > marketplace.
   *
   * Merch outranks marketplace because merch always ships free for members,
   * so a merch item should never contribute toward the member $75 threshold.
   */
  function analyse(cart) {
    var out = {
      empty: true,
      isMember: window.WH_IS_MEMBER === true,
      marketplaceTotal: 0,
      merchTotal: 0,
      boxTotal: 0,
      uncategorizedTotal: 0,
      hasBox: false,
      hasMembershipInCart: false,
      breakdown: []
    };

    if (!cart || !cart.items || !cart.items.length) return out;
    out.empty = false;

    cart.items.forEach(function (item) {
      var line = item.final_line_price != null ? item.final_line_price : item.line_price;

      if (
        item.variant_id === CONFIG.membershipVariantId ||
        item.handle === CONFIG.membershipHandle
      ) {
        out.hasMembershipInCart = true;
        out.breakdown.push({ title: item.product_title, bucket: 'membership (excluded)', amount: money(line) });
        return;
      }

      var pid = item.product_id;
      var bucket;

      if (inAnyBox(pid)) {
        out.hasBox = true;
        out.boxTotal += line;
        bucket = 'box (excluded from thresholds)';
      } else if (inCollection(pid, CONFIG.merchHandle)) {
        out.merchTotal += line;
        bucket = 'merch';
      } else if (inCollection(pid, CONFIG.marketplaceHandle)) {
        out.marketplaceTotal += line;
        bucket = 'marketplace';
      } else {
        out.uncategorizedTotal += line;
        bucket = 'uncategorized (excluded)';
      }

      out.breakdown.push({ title: item.product_title, bucket: bucket, amount: money(line) });
    });

    if (out.hasMembershipInCart) out.isMember = true;
    return out;
  }

  function resolveState(a) {
    var hidden = { visible: false };
    if (a.empty) return hidden;

    if (a.isMember) {
      if (a.marketplaceTotal === 0) {
        return {
          visible: true,
          showBar: false,
          line: 'Boxes &amp; Merch always ship free with your membership.',
          showUpsell: false
        };
      }
      if (a.marketplaceTotal >= CONFIG.memberThreshold) {
        return {
          visible: true, showBar: true, percent: 100, complete: true,
          line: 'Free shipping unlocked!',
          showUpsell: false
        };
      }
      return {
        visible: true, showBar: true,
        percent: (a.marketplaceTotal / CONFIG.memberThreshold) * 100,
        line:
          'Boxes &amp; Merch always ship free &middot; Marketplace orders ship free at $75 — you\u2019re <strong>' +
          money(CONFIG.memberThreshold - a.marketplaceTotal) + '</strong> away',
        showUpsell: false
      };
    }

    var eligible = a.marketplaceTotal + a.merchTotal;

    if (eligible === 0) {
      if (a.hasBox) {
        return {
          visible: true, showBar: false,
          line: 'Boxes ship flat-rate at ' + CONFIG.boxFlatRate + '.',
          showUpsell: true
        };
      }
      return hidden;
    }

    var base = {
      visible: true, showBar: true, showUpsell: true,
      caveat: a.hasBox
        ? 'Boxes ship flat-rate at ' + CONFIG.boxFlatRate +
          ' and don\u2019t count toward the $110.'
        : null
    };

    if (eligible >= CONFIG.nonMemberThreshold) {
      base.percent = 100;
      base.complete = true;
      base.line = 'Free shipping unlocked on your Marketplace &amp; Merch order!';
      return base;
    }

    base.percent = (eligible / CONFIG.nonMemberThreshold) * 100;
    base.line =
      CONFIG.standardFlatRate +
      ' flat-rate shipping &middot; Marketplace &amp; Merch items ship free at $110 — you\u2019re <strong>' +
      money(CONFIG.nonMemberThreshold - eligible) + '</strong> away';
    return base;
  }

  // -------------------------------------------------------------------
  // Styles
  // -------------------------------------------------------------------
  function injectStyles() {
    if (document.getElementById('wh-cart-ship-bar-styles')) return;
    var c = CONFIG.color;
    var css =
      '#' + CONFIG.mountId + '{display:block;width:100%;}' +
      '.whcb-root{box-sizing:border-box;width:100%;padding:14px 16px 16px;' +
        'font-family:"EB Garamond",Garamond,serif;color:' + c.espresso + ';}' +
      '.whcb-root *{box-sizing:border-box;}' +
      '.whcb-line{font-size:14px;line-height:1.45;margin:0 0 10px;letter-spacing:.01em;}' +
      '.whcb-line strong{font-weight:700;color:' + c.teal + ';}' +
      '.whcb-root--nobar .whcb-line{margin-bottom:0;}' +
      // Track: no child element. Progress is painted as an inline gradient.
      '.whcb-track{display:block;position:relative;width:100%;height:8px;min-height:8px;' +
        'border-radius:999px;overflow:hidden;background-color:' + c.beige + ';' +
        'background-repeat:no-repeat;}' +
      '.whcb-caveat{margin:9px 0 0;font-size:12.5px;line-height:1.45;color:rgba(50,51,51,.68);}' +
      '.whcb-upsell{display:inline-block;margin:11px 0 0;padding:0;border:0;background:none;' +
        'font-family:"EB Garamond",Garamond,serif;font-size:13px;line-height:1.4;color:' + c.teal + ';' +
        'text-align:left;cursor:pointer;border-bottom:1px solid ' + c.azul + ';' +
        'transition:color .2s ease,border-color .2s ease;}' +
      '.whcb-upsell:hover{color:' + c.chartreuse + ';border-color:' + c.chartreuse + ';}' +
      '.whcb-upsell[disabled]{opacity:.55;cursor:default;}' +
      '.whcb-upsell strong{font-weight:700;}';

    var tag = document.createElement('style');
    tag.id = 'wh-cart-ship-bar-styles';
    tag.textContent = css;
    document.head.appendChild(tag);
  }

  // -------------------------------------------------------------------
  // Bar painting — inline gradient, !important, no child element
  // -------------------------------------------------------------------
  function writeGradient(track, percent, complete) {
    var p = Math.max(0, Math.min(100, percent));
    var lead = complete ? CONFIG.color.chartreuse : CONFIG.color.teal;
    var rest = CONFIG.color.beige;

    var value =
      'linear-gradient(to right, ' +
      lead + ' 0%, ' + lead + ' ' + p.toFixed(2) + '%, ' +
      rest + ' ' + p.toFixed(2) + '%, ' + rest + ' 100%)';

    track.style.setProperty('background-image', value, 'important');
    track.style.setProperty('background-color', rest, 'important');
    track.setAttribute('data-wh-percent', p.toFixed(2));
  }

  function animateBar(track, target, complete) {
    if (state.tweenId) cancelAnimationFrame(state.tweenId);

    var start = state.paintedPercent || 0;
    var delta = target - start;

    if (Math.abs(delta) < 0.5) {
      writeGradient(track, target, complete);
      state.paintedPercent = target;
      return;
    }

    var duration = 450;
    var t0 = null;

    function step(ts) {
      if (t0 === null) t0 = ts;
      var progress = Math.min(1, (ts - t0) / duration);
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = start + delta * eased;
      writeGradient(track, current, complete);
      state.paintedPercent = current;
      if (progress < 1) {
        state.tweenId = requestAnimationFrame(step);
      } else {
        state.tweenId = null;
        state.paintedPercent = target;
      }
    }

    state.tweenId = requestAnimationFrame(step);
  }

  // -------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------
  function buildRoot() {
    var root = document.createElement('div');
    root.className = 'whcb-root';
    root.innerHTML =
      '<p class="whcb-line"></p>' +
      '<div class="whcb-track" role="progressbar" aria-valuemin="0" aria-valuemax="100"></div>' +
      '<p class="whcb-caveat" style="display:none"></p>' +
      '<button type="button" class="whcb-upsell" style="display:none">' +
        'Members ship free at $75. <strong>Join for $97/yr &rarr;</strong>' +
      '</button>';

    root.querySelector('.whcb-upsell').addEventListener('click', onUpsellClick);
    return root;
  }

  function getMount() {
    return document.getElementById(CONFIG.mountId);
  }

  /**
   * Rebuy's stylesheet sets display on generic elements inside the cart, which
   * outranks the HTML `hidden` attribute. These write an inline !important
   * display value instead, which nothing in Rebuy can override.
   */
  function hide(el) {
    if (el) el.style.setProperty('display', 'none', 'important');
  }

  function show(el, mode) {
    if (el) el.style.setProperty('display', mode || 'block', 'important');
  }

  function paint() {
    var mount = getMount();
    if (!mount) return;

    if (!state.rootNode) state.rootNode = buildRoot();
    var root = state.rootNode;

    var view = state.ready ? resolveState(analyse(state.cart)) : { visible: false };

    state.writing = true;

    if (!view.visible) {
      hide(mount);
      state.writing = false;
      return;
    }

    show(mount, 'block');
    if (root.parentNode !== mount) {
      mount.innerHTML = '';
      mount.appendChild(root);
      state.paintedPercent = 0; // fresh node, restart the tween from zero
    }

    root.querySelector('.whcb-line').innerHTML = view.line;

    var track = root.querySelector('.whcb-track');
    if (view.showBar) {
      show(track, 'block');
      track.setAttribute('aria-valuenow', Math.round(view.percent));
      animateBar(track, view.percent, !!view.complete);
    } else {
      hide(track);
    }

    root.classList.toggle('whcb-root--nobar', !view.showBar);

    var caveat = root.querySelector('.whcb-caveat');
    if (view.caveat) {
      caveat.textContent = view.caveat;
      show(caveat, 'block');
    } else {
      hide(caveat);
    }

    var upsell = root.querySelector('.whcb-upsell');
    if (view.showUpsell) {
      show(upsell, 'inline-block');
    } else {
      hide(upsell);
      resetUpsellButton(upsell);
    }

    state.writing = false;
  }

  // -------------------------------------------------------------------
  // Membership upsell
  // -------------------------------------------------------------------
  var UPSELL_LABEL =
    'Members ship free at $75. <strong>Join for $97/yr &rarr;</strong>';

  function resetUpsellButton(btn) {
    if (!btn) return;
    btn.innerHTML = UPSELL_LABEL;
    btn.disabled = false;
    btn.onclick = null; // clear the product-page fallback set by a failed add
  }

  function refreshRebuy() {
    var tried = [];
    try {
      var R = window.Rebuy;
      if (R) {
        if (R.SmartCart && typeof R.SmartCart.refresh === 'function') {
          R.SmartCart.refresh(); tried.push('Rebuy.SmartCart.refresh');
        }
        if (R.Cart && typeof R.Cart.refresh === 'function') {
          R.Cart.refresh(); tried.push('Rebuy.Cart.refresh');
        }
        if (R.SmartCart && typeof R.SmartCart.update === 'function') {
          R.SmartCart.update(); tried.push('Rebuy.SmartCart.update');
        }
        if (R.SmartCart && typeof R.SmartCart.getCart === 'function') {
          R.SmartCart.getCart(); tried.push('Rebuy.SmartCart.getCart');
        }
      }
    } catch (e) { /* keep going */ }

    ['rebuy:cart.refresh', 'rebuy:cart.change', 'cart:refresh', 'cart:build'].forEach(function (n) {
      document.dispatchEvent(new CustomEvent(n, { bubbles: true }));
    });

    return tried;
  }

  /**
   * Reads the membership product and returns a selling_plan id if the variant
   * requires one, otherwise null. Subscription-only variants are rejected by
   * /cart/add.js unless a selling plan is supplied.
   */
  function getMembershipSellingPlan() {
    return fetch('/products/' + CONFIG.membershipHandle + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (r) { return r.json(); })
      .then(function (product) {
        if (!product) return null;

        var variant = null;
        (product.variants || []).forEach(function (v) {
          if (v.id === CONFIG.membershipVariantId) variant = v;
        });
        if (!variant) variant = (product.variants || [])[0];

        // If one-time purchase is allowed, no selling plan needed.
        if (variant && variant.requires_selling_plan === false) return null;
        if (product.requires_selling_plan === false) return null;

        var groups = product.selling_plan_groups || [];
        for (var i = 0; i < groups.length; i++) {
          var plans = groups[i].selling_plans || [];
          if (plans.length) return plans[0].id;
        }
        return null;
      })
      .catch(function () { return null; });
  }

  function addMembership() {
    return getMembershipSellingPlan().then(function (planId) {
      var item = { id: CONFIG.membershipVariantId, quantity: 1 };
      if (planId) item.selling_plan = planId;

      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] })
      }).then(function (res) {
        return res.text().then(function (text) {
          var payload = null;
          try { payload = JSON.parse(text); } catch (e) { payload = { raw: text }; }

          if (!res.ok) {
            var err = new Error('cart/add failed');
            err.status = res.status;
            err.payload = payload;
            err.sellingPlanUsed = planId || null;
            throw err;
          }
          return payload;
        });
      });
    });
  }

  function onUpsellClick(e) {
    var btn = e.currentTarget;
    if (btn.disabled) return;

    btn.disabled = true;
    btn.innerHTML = 'Adding&hellip;';

    addMembership()
      .then(function () {
        state.lastAddError = null;
        refreshRebuy();
        return sync();
      })
      .then(function () {
        resetUpsellButton(btn); // hidden by paint() if they're now a member
      })
      .catch(function (err) {
        state.lastAddError = {
          status: err && err.status,
          payload: err && err.payload,
          sellingPlanUsed: err && err.sellingPlanUsed,
          message: err && err.message
        };

        console.error(
          '%c[WellHaus cart bar] Membership add failed —',
          'color:#B7856A;font-weight:bold',
          state.lastAddError
        );

        btn.disabled = false;
        btn.innerHTML =
          'Members ship free at $75. <strong>View membership &rarr;</strong>';
        btn.onclick = function () {
          window.location.href = '/products/' + CONFIG.membershipHandle;
        };
      });
  }

  // -------------------------------------------------------------------
  // Sync
  // -------------------------------------------------------------------
  function sync() {
    return fetchCart().then(function (cart) {
      if (!cart) return;
      state.cart = cart;
      state.ready = true;
      paint();
    });
  }

  var syncTimer = null;
  function scheduleSync(delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, delay == null ? 180 : delay);
  }

  // -------------------------------------------------------------------
  // Cart-change detection
  // -------------------------------------------------------------------
  function isCartEndpoint(url) {
    if (!url) return false;
    return /\/cart\/(add|change|update|clear)(\.js)?/.test(String(url));
  }

  function interceptCartRequests() {
    var origFetch = window.fetch;
    if (origFetch && !origFetch.__whPatched) {
      var patched = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url);
        return origFetch.apply(this, arguments).then(function (res) {
          if (isCartEndpoint(url)) scheduleSync(220);
          return res;
        });
      };
      patched.__whPatched = true;
      window.fetch = patched;
    }

    var origOpen = XMLHttpRequest.prototype.open;
    if (!origOpen.__whPatched) {
      var newOpen = function (method, url) {
        this.__whCartUrl = url;
        return origOpen.apply(this, arguments);
      };
      newOpen.__whPatched = true;
      XMLHttpRequest.prototype.open = newOpen;

      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var self = this;
        this.addEventListener('load', function () {
          if (isCartEndpoint(self.__whCartUrl)) scheduleSync(220);
        });
        return origSend.apply(this, arguments);
      };
    }
  }

  // -------------------------------------------------------------------
  // Rebuy re-render survival
  // -------------------------------------------------------------------
  function watchDom() {
    var observer = new MutationObserver(function (mutations) {
      if (state.writing) return;

      var relevant = false;
      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        if (t && t.nodeType === 1 && t.closest && t.closest('.whcb-root')) continue;
        relevant = true;
        break;
      }
      if (!relevant) return;

      var mount = getMount();
      if (!mount) return;

      if (state.rootNode && state.rootNode.parentNode !== mount) {
        paint();
      } else if (!state.ready) {
        scheduleSync(120);
      } else {
        scheduleSync(320);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // -------------------------------------------------------------------
  // Diagnostics
  // -------------------------------------------------------------------
  window.whCartBarDebug = function () {
    var a = analyse(state.cart);
    var mount = getMount();
    var track = state.rootNode ? state.rootNode.querySelector('.whcb-track') : null;

    var info = {
      mountFound: !!mount,
      attached: !!(state.rootNode && state.rootNode.parentNode),
      WH_IS_MEMBER: window.WH_IS_MEMBER,
      resolvedAsMember: a.isMember,
      membershipInCart: a.hasMembershipInCart,
      marketplaceTotal: money(a.marketplaceTotal),
      merchTotal: money(a.merchTotal),
      boxTotal: money(a.boxTotal),
      uncategorizedTotal: money(a.uncategorizedTotal),
      nonMemberEligible: money(a.marketplaceTotal + a.merchTotal),
      hasBox: a.hasBox,
      collectionsLoaded: Object.keys(state.collections).map(function (h) {
        return h + ': ' + state.collections[h].size + ' products';
      }),
      view: resolveState(a),
      barPercentAttr: track ? track.getAttribute('data-wh-percent') : null,
      barComputedBg: track ? getComputedStyle(track).backgroundImage.slice(0, 120) : null,
      trackRect: track ? track.getBoundingClientRect() : null,
      lastAddError: state.lastAddError,
      rebuyApis: {
        Rebuy: typeof window.Rebuy,
        SmartCart: window.Rebuy ? typeof window.Rebuy.SmartCart : 'n/a',
        SmartCartRefresh:
          window.Rebuy && window.Rebuy.SmartCart
            ? typeof window.Rebuy.SmartCart.refresh : 'n/a',
        SmartCartKeys:
          window.Rebuy && window.Rebuy.SmartCart
            ? Object.keys(window.Rebuy.SmartCart).slice(0, 30) : 'n/a'
      }
    };

    console.log('%cWellHaus cart shipping bar', 'color:#315144;font-weight:bold');
    console.table(a.breakdown);
    console.log(info);
    return info;
  };

  window.whCartBarTestAdd = function () {
    console.log('Testing membership add…');
    return getMembershipSellingPlan().then(function (planId) {
      console.log('Selling plan detected:', planId);
      return addMembership()
        .then(function (r) { console.log('SUCCESS', r); return r; })
        .catch(function (e) {
          console.error('FAILED', {
            status: e.status, payload: e.payload, sellingPlanUsed: e.sellingPlanUsed
          });
          throw e;
        });
    });
  };

  // -------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------
  function boot() {
    injectStyles();
    interceptCartRequests();
    watchDom();

    loadAllCollections().then(sync);

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) scheduleSync(200);
    });
    ['rebuy.loaded', 'rebuy:cart.change', 'cart:refresh'].forEach(function (n) {
      document.addEventListener(n, function () { scheduleSync(250); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();